import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChatCircleText, Plus, Trash, PencilSimple, CheckCircle, XCircle, Calendar, User, X } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = { interview_date: '', interviewer: '', feedback: '', result: 'Pass' };

const InterviewRounds = ({ candidateId }) => {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  const fetchRounds = async () => {
    try {
      const { data } = await axios.get(`${API}/candidates/${candidateId}/interviews`);
      setRounds(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, interview_date: new Date().toISOString().slice(0, 10) });
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      interview_date: (r.interview_date || '').slice(0, 10),
      interviewer: r.interviewer || '',
      feedback: r.feedback || '',
      result: r.result || 'Pass',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.interview_date || !form.interviewer.trim()) {
      toast.error('Interview date and interviewer are required');
      return;
    }
    if ((form.feedback || '').length > 1000) {
      toast.error('Feedback must be 1000 characters or fewer');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/interviews/${editingId}`, form);
        toast.success('Interview round updated');
      } else {
        await axios.post(`${API}/candidates/${candidateId}/interviews`, form);
        toast.success('Interview round added');
      }
      closeForm();
      fetchRounds();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save interview round');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this interview round?')) return;
    try {
      await axios.delete(`${API}/interviews/${id}`);
      toast.success('Interview round deleted');
      fetchRounds();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6" data-testid="interview-rounds-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <ChatCircleText size={22} weight="duotone" className="text-[#002FA7]" />
          <h2 className="text-xl font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Interview Rounds
          </h2>
          <span className="text-xs font-medium text-[#002FA7] bg-blue-50 px-2 py-0.5 rounded">
            {rounds.length} {rounds.length === 1 ? 'round' : 'rounds'}
          </span>
        </div>
        <button
          onClick={openCreate}
          className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium"
          data-testid="add-interview-button"
        >
          <Plus size={16} weight="bold" />
          <span>Add Round</span>
        </button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-slate-500">Loading interview rounds...</div>
      ) : rounds.length === 0 ? (
        <div className="py-8 text-center border border-dashed border-slate-200 rounded" data-testid="no-interviews">
          <p className="text-sm text-slate-500">
            No interview rounds yet. Add the first round to capture feedback.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="interviews-list">
          {rounds.map((r, idx) => {
            const isPass = r.result === 'Pass';
            return (
              <div
                key={r.id}
                className="border border-slate-200 rounded-lg p-4"
                data-testid={`interview-round-${idx + 1}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-[#002FA7] text-xs font-bold">
                      #{idx + 1}
                    </span>
                    <div>
                      <div className="flex items-center space-x-3 text-sm text-slate-600">
                        <span className="flex items-center space-x-1">
                          <Calendar size={14} />
                          <span>{formatDate(r.interview_date)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <User size={14} />
                          <span className="font-medium text-slate-800">{r.interviewer}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded text-xs font-semibold ${
                        isPass
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-red-50 text-red-700'
                      }`}
                      data-testid={`round-result-${idx + 1}`}
                    >
                      {isPass ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                      <span>{r.result}</span>
                    </span>
                    <button
                      onClick={() => openEdit(r)}
                      className="p-1.5 text-slate-500 hover:text-[#002FA7] hover:bg-slate-100 rounded transition-colors"
                      data-testid={`edit-round-${idx + 1}`}
                      title="Edit"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => remove(r.id)}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      data-testid={`delete-round-${idx + 1}`}
                      title="Delete"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
                {r.feedback && (
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap pl-10">
                    {r.feedback}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div
          className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !saving && closeForm()}
          data-testid="interview-form-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editingId ? 'Edit Interview Round' : 'Add Interview Round'}
              </h3>
              <button
                onClick={closeForm}
                className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                data-testid="close-form-button"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Interview Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.interview_date}
                  onChange={(e) => setForm({ ...form, interview_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="form-interview-date"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Interviewed By <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.interviewer}
                  onChange={(e) => setForm({ ...form, interviewer: e.target.value })}
                  placeholder="e.g. Jane Doe"
                  maxLength={255}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="form-interviewer"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Result <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-2">
                  {['Pass', 'Fail'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm({ ...form, result: opt })}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
                        form.result === opt
                          ? opt === 'Pass'
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                            : 'bg-red-50 border-red-500 text-red-700'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid={`form-result-${opt.toLowerCase()}`}
                    >
                      {opt === 'Pass' ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
                      <span>{opt}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Feedback <span className="text-slate-400 font-normal">(optional, max 1000 characters)</span>
                </label>
                <textarea
                  value={form.feedback}
                  onChange={(e) => setForm({ ...form, feedback: e.target.value.slice(0, 1000) })}
                  rows={5}
                  placeholder="Interview notes, strengths, gaps, recommendations..."
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="form-feedback"
                />
                <p className="text-xs text-slate-500 mt-1 text-right">
                  {form.feedback.length}/1000
                </p>
              </div>

              <div className="flex items-center justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
                  data-testid="form-cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary px-5 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                  data-testid="form-save-button"
                >
                  {saving ? 'Saving…' : editingId ? 'Update Round' : 'Add Round'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewRounds;
